/**
 * Utility functions for handling API errors safely
 */

/**
 * Safely extract an error message from an API error response.
 * Handles cases where detail might be an object (like 402 insufficient balance errors, 429 rate limit errors).
 * 
 * @param error - The error object from axios or similar
 * @param defaultMessage - Default message to use if extraction fails
 * @returns A string message safe to display in toasts or UI
 */
export function extractErrorMessage(error: any, defaultMessage: string = 'An error occurred'): string {
  // Handle 402 errors specially - don't extract message, let InsufficientBalanceModal handle it
  if (error?.response?.status === 402) {
    // Return empty string - caller should handle 402 differently
    return '';
  }

  const detail = error?.response?.data?.detail;
  
  if (!detail) {
    return error?.message || defaultMessage;
  }
  
  // If detail is an object, extract the message field (works for both 402 and 429 errors)
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
    // Prefer message field, fallback to error field, then stringify if needed
    return detail.message || detail.error || JSON.stringify(detail);
  }
  
  // If detail is already a string, use it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // Fallback
  return error?.message || defaultMessage;
}

/**
 * Check if an error is a 402 Payment Required (Insufficient Balance) error
 */
export function isInsufficientBalanceError(error: any): boolean {
  return error?.response?.status === 402;
}

